import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PricingService } from "@/gen/omnios/v1/pricing_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Timestamp } from "@bufbuild/protobuf";

export const Route = createFileRoute("/commerce/pricelists")({
	component: PriceListsPage,
});

const pricingClient = createClient(PricingService, transport);

function fmtTs(ts: Timestamp | undefined): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU");
}

function PriceListsPage() {
	const { t } = useTranslation("commerce");
	const navigate = useNavigate();
	useBreadcrumbs([{ label: t("pricelists") }]);

	const qc = useQueryClient();
	const [showCreate, setShowCreate] = useState(false);
	const [form, setForm] = useState({
		name: "",
		currency: "RUB",
		priority: 100,
		customerSegment: "",
		customerPartyId: "",
	});

	const { data, isLoading } = useQuery({
		queryKey: ["price-lists"],
		queryFn: () => pricingClient.listPriceLists({}),
	});

	const createMut = useMutation({
		mutationFn: () =>
			pricingClient.createPriceList({
				name: form.name,
				currency: form.currency,
				priority: form.priority,
				customerSegment: form.customerSegment,
				customerPartyId: form.customerPartyId,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["price-lists"] });
			setShowCreate(false);
			setForm({ name: "", currency: "RUB", priority: 100, customerSegment: "", customerPartyId: "" });
		},
	});

	const toggleMut = useMutation({
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
			pricingClient.updatePriceList({ id, isActive }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["price-lists"] }),
	});

	const priceLists = data?.priceLists ?? [];

	return (
		<div className="space-y-4 p-6">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">{t("pricelists")}</h2>
				<Button size="sm" onClick={() => setShowCreate(true)}>
					<Plus className="mr-1.5 h-3.5 w-3.5" />
					{t("common:create")}
				</Button>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : priceLists.length === 0 ? (
				<p className="py-8 text-center text-sm text-muted-foreground">
					{t("pl_empty")}
				</p>
			) : (
				<div className="overflow-hidden rounded-lg border">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/30">
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
									{t("pricelist_name")}
								</TableHead>
								<TableHead className="w-20 px-3 py-2 text-left font-medium text-muted-foreground">
									{t("pricelist_currency")}
								</TableHead>
								<TableHead className="w-20 px-3 py-2 text-left font-medium text-muted-foreground">
									{t("pl_col_active")}
								</TableHead>
								<TableHead className="w-20 px-3 py-2 text-left font-medium text-muted-foreground">
									{t("pricelist_priority")}
								</TableHead>
								<TableHead className="w-40 px-3 py-2 text-left font-medium text-muted-foreground">
									{t("pricelist_effective")}
								</TableHead>
								<TableHead className="w-20 px-3 py-2 text-left font-medium text-muted-foreground">
									{t("pricelist_rules")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody className="divide-y">
							{priceLists.map((pl) => (
								<TableRow
									key={pl.id}
									className="cursor-pointer hover:bg-muted/50"
									onClick={() => navigate({ to: `/commerce/pricelists/${pl.id}` as string })}
								>
									<TableCell className="px-3 py-2">
										<div className="font-medium">{pl.name}</div>
										{pl.customerSegment && (
											<div className="text-xs text-muted-foreground">
												{t("pl_segment")}: {pl.customerSegment}
											</div>
										)}
									</TableCell>
									<TableCell className="px-3 py-2 text-muted-foreground">{pl.currency}</TableCell>
									<TableCell className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
										<Switch
											checked={pl.isActive}
											onCheckedChange={(v) => toggleMut.mutate({ id: pl.id, isActive: v })}
										/>
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{pl.priority}
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{fmtTs(pl.effectiveFrom)} — {fmtTs(pl.effectiveUntil)}
									</TableCell>
									<TableCell className="px-3 py-2">
										<Badge variant="outline" className="text-xs">
											{pl.rules.length}
										</Badge>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{/* Create dialog */}
			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("pl_create")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("pricelist_name")}</Label>
							<Input
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder={t("pl_name_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("pricelist_currency")}</Label>
							<Select
								value={form.currency}
								onValueChange={(v) => setForm({ ...form, currency: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="RUB">RUB</SelectItem>
									<SelectItem value="USD">USD</SelectItem>
									<SelectItem value="EUR">EUR</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t("pricelist_priority")}</Label>
							<Input
								type="number"
								value={form.priority}
								onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("pl_customer_segment")}</Label>
							<Input
								value={form.customerSegment}
								onChange={(e) => setForm({ ...form, customerSegment: e.target.value })}
								placeholder={t("pl_segment_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("pl_customer_party")}</Label>
							<Input
								value={form.customerPartyId}
								onChange={(e) => setForm({ ...form, customerPartyId: e.target.value })}
								placeholder="UUID"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowCreate(false)}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => createMut.mutate()}
							disabled={!form.name || createMut.isPending}
						>
							{createMut.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
							{t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
